import { notifyUnauthorized } from "../auth/sessionEvents";

// Use same-origin API routing by default; the reverse proxy forwards /api to the backend.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

type ApiFetchOptions = Omit<RequestInit, "body"> & { body?: any; skipJsonParse?: boolean };

const isJsonContent = (response: Response) => {
  const ct = response.headers.get("content-type");
  return ct ? ct.includes("application/json") : false;
};

export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { skipJsonParse, headers, body, ...rest } = options;
  const url = `${API_BASE_URL}${path}`;

  const isFormData = body instanceof FormData;
  const mergedHeaders = new Headers(headers || {});
  mergedHeaders.set("Accept", "application/json");

  // Attach auth token if available and not already provided
  if (!mergedHeaders.has("Authorization")) {
    const token = localStorage.getItem("pvapp_token");
    if (token) {
      mergedHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  let finalBody = body;
  if (!isFormData && body !== undefined) {
    const shouldStringify = typeof body === "object" && !(body instanceof Blob) && !(body instanceof ArrayBuffer);
    if (shouldStringify) {
      if (!mergedHeaders.has("Content-Type")) {
        mergedHeaders.set("Content-Type", "application/json");
      }
      finalBody = JSON.stringify(body);
    }
  }

  const response = await fetch(url, {
    ...rest,
    headers: mergedHeaders,
    body: finalBody,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const data = isJsonContent(response) ? await response.json() : await response.text();
      detail = (data as any)?.detail || (typeof data === "string" ? data : JSON.stringify(data));
    } catch (_) {
      /* ignore */
    }
    if (response.status === 401 && !path.startsWith("/auth/login")) {
      notifyUnauthorized(detail);
    }
    throw new Error(`API error ${response.status}: ${detail}`);
  }

  if (response.status === 204 || skipJsonParse) {
    return undefined as unknown as T;
  }

  if (!isJsonContent(response)) {
    const text = await response.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  return (await response.json()) as T;
}
